import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

const BASE = '/api';

function authFetch(url, opts = {}) {
  const token = localStorage.getItem('arcus_token');
  return fetch(url, {
    ...opts,
    headers: { ...(opts.headers||{}), ...(token?{Authorization:`Bearer ${token}`}:{}) },
  });
}
async function api(method, path, body) {
  const res = await authFetch(`${BASE}${path}`, {
    method, headers:{'Content-Type':'application/json'},
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) { const e = await res.json().catch(()=>({})); throw new Error(e.error||`Erreur ${res.status}`); }
  return res.json();
}
async function dlDoc(amendId, type) {
  const res = await authFetch(`${BASE}/documents/amendement/${amendId}/${type}`);
  if (!res.ok) { const e=await res.json().catch(()=>({})); alert(e.error||'Error'); return; }
  const blob=await res.blob(); const url=URL.createObjectURL(blob);
  const a=document.createElement('a'); a.href=url;
  const d=res.headers.get('Content-Disposition')||''; const m=d.match(/filename="(.+)"/);
  a.download=m?m[1]:`${type}_amendment.docx`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
}

const MAJ_K = ['maj_sports_mariages','maj_hors_ue','maj_usa_canada','maj_groupes','maj_transfert','maj_activites_risque'];
const MAJ_C = [2.00,1.25,1.10,1.25,2.00,2.00];
const MIN_K = ['min_historique_vierge','min_experience_dirigeant'];
const MIN_C = [0.90,0.90];

const fmt2 = n => n!=null ? new Intl.NumberFormat('fr-FR',{minimumFractionDigits:2}).format(parseFloat(n))+' €' : '—';
const fmtFR = d => { if(!d) return '—'; return new Date(d).toLocaleDateString('fr-FR'); };
const toISO = d => { if(!d) return ''; return new Date(d).toISOString().split('T')[0]; };
const lbl = { fontSize:12, color:'#64748B', display:'block', marginBottom:3, fontWeight:600 };
const inp = { border:'1px solid #E2E8F0', borderRadius:6, padding:'7px 10px', width:'100%', fontSize:14, boxSizing:'border-box', fontFamily:'inherit' };
const lockedField = { background:'#F8FAFC', border:'1px solid #F1F5F9', borderRadius:6, padding:'7px 10px', fontSize:14, color:'#94A3B8', minHeight:35 };

export default function FicheAmendement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Dynamic translated arrays — inside component
  const MAJ_Q = [t('polices.maj_q1'),t('polices.maj_q2'),t('polices.maj_q3'),t('polices.maj_q4'),t('polices.maj_q5'),t('polices.maj_q6')];
  const MIN_Q = [t('polices.min_q1'),t('polices.min_q2')];
  const ACTIVITES = [t('polices.act1'),t('polices.act2'),t('polices.act3'),t('polices.act4'),t('polices.act5')];
  const FRACS = [
    {v:'annuel',l:t('polices.freq_annual')},{v:'semestriel',l:t('polices.freq_semi')},
    {v:'trimestriel',l:t('polices.freq_quarter')},{v:'mensuel',l:t('polices.freq_month')},
  ];
  const MOTIFS = [
    {v:'echeance',          l:t('resiliation.motif_echeance'),    desc:t('resiliation.motif_echeance_desc'),    icon:'📅'},
    {v:'disparition_risque',l:t('resiliation.motif_disparition'), desc:t('resiliation.motif_disparition_desc'), icon:'🔕', needDate:true, prorata:true},
    {v:'aggravation_risque',l:t('resiliation.motif_aggravation'), desc:t('resiliation.motif_aggravation_desc'), icon:'⚠️', needNotif:true, prorata:true},
    {v:'non_paiement',      l:t('resiliation.motif_nonpaiement'), desc:t('resiliation.motif_nonpaiement_desc'), icon:'💳', needDate:true},
  ];
  const SLIDES = [t('amendements.tab_amendment'), t('amendements.tab_underwriting'), t('amendements.tab_documents')];

  const [amend, setAmend]       = useState(null);
  const [slide, setSlide]       = useState(0);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [tarif, setTarif]       = useState(null);
  const calcTimer               = useRef(null);

  const [showActivation, setShowActivation] = useState(false);
  const [activating, setActivating]         = useState(false);
  const [showResil, setShowResil]           = useState(false);
  const [resilMotif, setResilMotif]         = useState('');
  const [resilDate, setResilDate]           = useState('');
  const [resilNotif, setResilNotif]         = useState('');
  const [resilNote, setResilNote]           = useState('');
  const [resilProrata, setResilProrata]     = useState(null);
  const [resilSaving, setResilSaving]       = useState(false);
  const [showDelete, setShowDelete]         = useState(false);

  const isLocked = amend && (amend.statut==='actif'||amend.statut==='remplace'||amend.statut==='resilie');
  const isProjet = amend?.statut==='projet';
  const isActif  = amend?.statut==='actif';

  useEffect(() => {
    if (!id) return;
    api('GET', `/amendements/${id}`).then(d => {
      setAmend(d);
      if (d.prime_ttc) setTarif({
        prime_ht:parseFloat(d.prime_ht), commission:parseFloat(d.commission),
        taxe:parseFloat(d.taxe), prime_ttc:parseFloat(d.prime_ttc),
      });
    }).catch(e => setError(e.message));
  }, [id]);

  function update(field, value) {
    if (isLocked) return;
    const updated = {...amend, [field]:value};
    setAmend(updated);
    const TARIF = ['ca_declare','fractionnement','deja_assure',...MAJ_K,...MIN_K,
      'sin_total_paye_clos','sin_nb_clos','sin_provision_ouverts','sin_part_paye_ouverts','sin_nb_ouverts','sin_nb_annees'];
    if (TARIF.includes(field)) {
      clearTimeout(calcTimer.current);
      calcTimer.current = setTimeout(() => saveAndCalc(updated), 600);
    }
  }

  function toggleActivite(label) {
    if (isLocked) return;
    const cur = amend.activites_couvertes || [];
    update('activites_couvertes', cur.includes(label)?cur.filter(a=>a!==label):[...cur,label]);
  }

  async function saveAndCalc(data) {
    try {
      const res = await api('PUT', `/amendements/${id}`, data);
      if (res.prime_ttc) setTarif({
        prime_ht:parseFloat(res.prime_ht), commission:parseFloat(res.commission),
        taxe:parseFloat(res.taxe), prime_ttc:parseFloat(res.prime_ttc),
      });
      if (res.difference_prime != null) setAmend(p=>({...p, difference_prime:res.difference_prime, sens_amendement:res.sens_amendement, prime_ttc:res.prime_ttc}));
    } catch(e) { /* silent */ }
  }

  async function save() {
    setSaving(true); setError('');
    try { await api('PUT', `/amendements/${id}`, amend); }
    catch(e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function activer() {
    setActivating(true);
    try { await api('POST', `/amendements/${id}/activer`, {}); navigate(-1); }
    catch(e) { setError(e.message); setActivating(false); setShowActivation(false); }
  }

  function calcProrata(dateResil) {
    if (!amend?.prime_ttc) return null;
    const dEff=amend.date_effet?new Date(amend.date_effet):null;
    const dEch=amend.date_echeance?new Date(amend.date_echeance):null;
    if (!dEff||!dEch) return null;
    const dR=new Date(dateResil);
    const total=Math.round((dEch-dEff)/86400000), used=Math.round((dR-dEff)/86400000), rem=Math.max(0,total-used);
    return {joursTotal:total, joursRestants:rem, montant:Math.round((rem/total)*parseFloat(amend.prime_ttc)*100)/100};
  }

  async function resilier() {
    setResilSaving(true);
    try {
      const body={motif:resilMotif, note_resiliation:resilNote,
        date_notification:resilMotif==='aggravation_risque'?resilNotif:undefined,
        date_resiliation_input:['disparition_risque','non_paiement'].includes(resilMotif)?resilDate:undefined};
      const res=await authFetch(`${BASE}/amendements/${id}/resilier`,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}).then(r=>r.json());
      if (res.statut==='resilie') { setAmend(a=>({...a,statut:'resilie'})); setShowResil(false); }
      else setError(res.error||t('common.error'));
    } catch(e) { setError(e.message); }
    finally { setResilSaving(false); }
  }

  async function supprimer() {
    try { await api('DELETE', `/amendements/${id}`, null); navigate(-1); }
    catch(e) { alert(e.message); setShowDelete(false); }
  }

  if (!amend) return <div style={{padding:24,color:'#64748B'}}>{error||t('common.loading')}</div>;

  const diff = parseFloat(amend.difference_prime)||0;
  const sens = amend.sens_amendement;
  const precTTC = parseFloat(amend.prime_ttc_precedent)||0;
  const newTTC  = tarif?.prime_ttc || parseFloat(amend.prime_ttc)||0;

  const STATUT_STYLE = {
    projet:   {bg:'#FEF3C7',color:'#92400E'},
    actif:    {bg:'#D1FAE5',color:'#065F46'},
    remplace: {bg:'#F1F5F9',color:'#94A3B8'},
    resilie:  {bg:'#FEE2E2',color:'#991B1B'},
  };
  const STATUT_LABELS = {
    projet:   t('status.projet_avenant'),
    actif:    t('status.avenant_actif'),
    remplace: t('status.remplace'),
    resilie:  t('status.resilie'),
  };
  const sc = STATUT_STYLE[amend.statut]||STATUT_STYLE.projet;

  // ── SLIDE 1 — L'AVENANT ──────────────────────────────────────
  function renderS1() {
    return (
      <div>
        {/* Contrat remplacé */}
        <div style={{background:'#F8FAFC',borderRadius:8,padding:'11px 14px',marginBottom:16,border:'1px solid #F1F5F9'}}>
          <div style={{fontSize:11,color:'#94A3B8',marginBottom:6,textTransform:'uppercase',letterSpacing:'.06em',fontWeight:700}}>
            {t('amendements.replaced_contract')}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
            <div><div style={{fontSize:11,color:'#64748B'}}>{t('amendements.current_policy')}</div><div style={{fontSize:14,fontWeight:700}}>{amend.n_police||amend.n_devis||'—'}</div></div>
            <div><div style={{fontSize:11,color:'#64748B'}}>{t('amendements.current_premium')}</div><div style={{fontSize:14,fontWeight:700,color:'#059669'}}>{fmt2(precTTC)}</div></div>
            <div><div style={{fontSize:11,color:'#64748B'}}>{t('amendements.client')}</div><div style={{fontSize:14,fontWeight:700}}>{amend.client_nom}</div></div>
          </div>
        </div>

        {/* Date d'effet */}
        <div style={{marginBottom:14,maxWidth:280}}>
          <label style={lbl}>{t('amendements.effect_date')}</label>
          {isLocked
            ? <div style={lockedField}>{fmtFR(amend.date_effet)}</div>
            : <>
                <input type="date" value={toISO(amend.date_effet)} onChange={e=>update('date_effet',e.target.value)} style={inp}/>
                <div style={{fontSize:12,color:'#64748B',marginTop:4}}>{t('amendements.effect_date_hint')}</div>
              </>
          }
        </div>

        {/* Notes */}
        <div>
          <label style={lbl}>{t('amendements.notes_label')}</label>
          {isLocked
            ? <div style={{...lockedField,minHeight:50}}>{amend.notes_internes||'—'}</div>
            : <textarea value={amend.notes_internes||''} onChange={e=>update('notes_internes',e.target.value)}
                placeholder={t('amendements.notes_placeholder')}
                style={{...inp,minHeight:70,resize:'vertical'}}/>
          }
        </div>

        {/* Résiliation info */}
        {amend.statut==='resilie'&&amend.motif_resiliation&&(
          <div style={{marginTop:14,background:'#FEE2E2',border:'1px solid #FCA5A5',borderRadius:8,padding:'11px 14px'}}>
            <div style={{fontSize:13,fontWeight:700,color:'#991B1B',marginBottom:8}}>{t('amendements.resiliation_info_title')}</div>
            <div style={{fontSize:13,color:'#7F1D1D'}}>
              <strong>{t('amendements.resiliation_motif')} :</strong> {amend.motif_resiliation} · <strong>{t('amendements.resiliation_date')} :</strong> {fmtFR(amend.date_resiliation)}
              {amend.prorata_rembourse>0&&<span> · <strong>{t('amendements.resiliation_prorata')} :</strong> {fmt2(amend.prorata_rembourse)}</span>}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── SLIDE 2 — TARIFICATION ────────────────────────────────────
  function renderS2() {
    return (
      <div>
        {/* Context badges */}
        <div style={{display:'flex',gap:7,marginBottom:14,flexWrap:'wrap'}}>
          {[`🌍 ${amend.marche_pays}`,
            amend.type_relation==='direct'?`⚡ ${t('polices.direct_plus15')}`:`🤝 ${t('polices.brokered_comm').replace('?',amend.courtier_commission||'?')}`,
          ].map((txt,i)=><span key={i} style={{fontSize:12,padding:'3px 10px',borderRadius:99,background:'#F1F5F9',border:'1px solid #E2E8F0',color:'#475569'}}>{txt}</span>)}
        </div>

        {/* CA + Frac */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:14}}>
          <div>
            <label style={lbl}>{t('polices.declared_ca')}</label>
            {isLocked
              ? <div style={lockedField}>{amend.ca_declare?new Intl.NumberFormat('fr-FR').format(amend.ca_declare)+' €':'—'}</div>
              : <input type="number" value={amend.ca_declare||''} onChange={e=>update('ca_declare',parseFloat(e.target.value)||0)} placeholder="Ex. 2 800 000" style={inp}/>
            }
          </div>
          <div>
            <label style={lbl}>{t('polices.payment_freq')}</label>
            {isLocked
              ? <div style={lockedField}>{FRACS.find(f=>f.v===amend.fractionnement)?.l||t('polices.freq_annual')}</div>
              : <select value={amend.fractionnement||'annuel'} onChange={e=>update('fractionnement',e.target.value)} style={inp}>
                  {FRACS.map(f=><option key={f.v} value={f.v}>{f.l}</option>)}
                </select>
            }
          </div>
        </div>

        {/* Majorations */}
        <div style={{fontSize:11,fontWeight:700,color:'#64748B',textTransform:'uppercase',letterSpacing:'.06em',paddingBottom:6,borderBottom:'1px solid #F1F5F9',marginBottom:8}}>{t('polices.loading_factors')}</div>
        {MAJ_Q.map((q,i)=>{
          const val=amend[MAJ_K[i]];
          return (
            <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 0',borderBottom:'1px solid #F8FAFC'}}>
              <div style={{flex:1,fontSize:13,color:'#475569'}}>{q}</div>
              {isLocked
                ? <span style={{fontSize:12,fontWeight:600,color:val?'#DC2626':'#94A3B8'}}>{val?`${t('common.yes')} ×${MAJ_C[i].toFixed(2)}`:t('common.no')}</span>
                : <>
                    <div style={{display:'flex',gap:5}}>
                      {[{v:true,l:t('common.yes')},{v:false,l:t('common.no')}].map(o=>(
                        <button key={String(o.v)} onClick={()=>update(MAJ_K[i],o.v)}
                          style={{padding:'3px 10px',borderRadius:99,fontSize:12,cursor:'pointer',
                            background:val===o.v?(o.v?'#FEE2E2':'#F1F5F9'):'#fff',
                            color:val===o.v?(o.v?'#DC2626':'#475569'):'#94A3B8',
                            border:`1px solid ${val===o.v?(o.v?'#FCA5A5':'#CBD5E1'):'#E2E8F0'}`,fontWeight:val===o.v?700:400}}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                    {val&&<span style={{fontSize:11,fontWeight:700,color:'#DC2626',width:36,textAlign:'right'}}>×{MAJ_C[i].toFixed(2)}</span>}
                  </>
              }
            </div>
          );
        })}

        {/* Minorations */}
        <div style={{fontSize:11,fontWeight:700,color:'#64748B',textTransform:'uppercase',letterSpacing:'.06em',padding:'12px 0 6px',borderBottom:'1px solid #F1F5F9',marginBottom:8}}>{t('polices.discount_factors')}</div>
        {MIN_Q.map((q,i)=>{
          const val=amend[MIN_K[i]];
          return (
            <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 0',borderBottom:'1px solid #F8FAFC'}}>
              <div style={{flex:1,fontSize:13,color:'#475569'}}>{q}</div>
              {isLocked
                ? <span style={{fontSize:12,fontWeight:600,color:val?'#059669':'#94A3B8'}}>{val?`${t('common.yes')} ×${MIN_C[i].toFixed(2)}`:t('common.no')}</span>
                : <>
                    <div style={{display:'flex',gap:5}}>
                      {[{v:true,l:t('common.yes')},{v:false,l:t('common.no')}].map(o=>(
                        <button key={String(o.v)} onClick={()=>update(MIN_K[i],o.v)}
                          style={{padding:'3px 10px',borderRadius:99,fontSize:12,cursor:'pointer',
                            background:val===o.v?(o.v?'#D1FAE5':'#F1F5F9'):'#fff',
                            color:val===o.v?(o.v?'#065F46':'#475569'):'#94A3B8',
                            border:`1px solid ${val===o.v?(o.v?'#6EE7B7':'#CBD5E1'):'#E2E8F0'}`,fontWeight:val===o.v?700:400}}>
                          {o.l}
                        </button>
                      ))}
                    </div>
                    {val&&<span style={{fontSize:11,fontWeight:700,color:'#059669',width:36,textAlign:'right'}}>×{MIN_C[i].toFixed(2)}</span>}
                  </>
              }
            </div>
          );
        })}

        {/* Activités */}
        <div style={{fontSize:11,fontWeight:700,color:'#64748B',textTransform:'uppercase',letterSpacing:'.06em',padding:'12px 0 6px',borderBottom:'1px solid #F1F5F9',marginBottom:8}}>{t('polices.covered_activities')}</div>
        {ACTIVITES.map((a,i)=>(
          <label key={i} style={{display:'flex',alignItems:'flex-start',gap:8,padding:'7px 0',borderBottom:'1px solid #F8FAFC',cursor:isLocked?'default':'pointer',fontSize:13,color:'#475569'}}>
            <input type="checkbox" checked={(amend.activites_couvertes||[]).includes(a)} onChange={()=>!isLocked&&toggleActivite(a)} disabled={isLocked} style={{marginTop:2}}/>
            {a}
          </label>
        ))}

        {/* Bloc comparaison */}
        {newTTC > 0 && (
          <div style={{marginTop:18,border:`1.5px solid ${sens==='augmentation'?'#93C5FD':sens==='diminution'?'#6EE7B7':'#CBD5E1'}`,borderRadius:10,overflow:'hidden'}}>
            <div style={{background:sens==='augmentation'?'#EFF6FF':sens==='diminution'?'#F0FDF4':'#F8FAFC',padding:'8px 12px',fontSize:13,fontWeight:700,
              color:sens==='augmentation'?'#1D4ED8':sens==='diminution'?'#065F46':'#475569'}}>
              {t('amendements.comparison_title')}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',background:'#fff'}}>
              <div style={{padding:'12px 14px',borderRight:'1px solid #F1F5F9'}}>
                <div style={{fontSize:11,color:'#94A3B8',marginBottom:3}}>{t('amendements.current_contract')}</div>
                <div style={{fontSize:17,fontWeight:700}}>{fmt2(precTTC)}</div>
              </div>
              <div style={{padding:'12px 14px',borderRight:'1px solid #F1F5F9'}}>
                <div style={{fontSize:11,color:'#94A3B8',marginBottom:3}}>{t('amendements.amendment_label')}</div>
                <div style={{fontSize:17,fontWeight:700}}>{fmt2(newTTC)}</div>
              </div>
              <div style={{padding:'12px 14px',background:sens==='augmentation'?'#EFF6FF':sens==='diminution'?'#F0FDF4':'#F8FAFC'}}>
                <div style={{fontSize:11,color:sens==='augmentation'?'#1D4ED8':'#065F46',marginBottom:3}}>{t('amendements.difference')}</div>
                <div style={{fontSize:17,fontWeight:700,color:sens==='augmentation'?'#1D4ED8':'#065F46'}}>
                  {diff>0?'+':''}{fmt2(diff)}
                </div>
              </div>
            </div>
            <div style={{padding:'8px 12px',background:'#F8FAFC',borderTop:'1px solid #F1F5F9',fontSize:12,color:'#64748B'}}>
              {sens==='augmentation'&&<span>{t('amendements.increase_msg')} <strong style={{color:'#1D4ED8'}}>{fmt2(diff)}</strong></span>}
              {sens==='diminution'  &&<span>{t('amendements.decrease_msg')}</span>}
              {sens==='stable'      &&<span>{t('amendements.stable_msg')}</span>}
            </div>
          </div>
        )}

        {/* Prime détaillée */}
        {tarif && !isLocked && (
          <div style={{marginTop:14,background:'#D1FAE5',borderRadius:8,padding:'12px 16px',border:'1px solid #6EE7B7'}}>
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
              {[{l:t('polices.net_premium'),v:fmt2(tarif.prime_ht)},{l:t('polices.commission'),v:fmt2(tarif.commission)},{l:t('polices.tax'),v:fmt2(tarif.taxe)},{l:t('polices.gross_premium'),v:fmt2(tarif.prime_ttc),big:true}].map((x,i)=>(
                <div key={i}><div style={{fontSize:10.5,color:'#065F46',marginBottom:2}}>{x.l}</div><div style={{fontSize:x.big?17:14,fontWeight:700,color:'#065F46'}}>{x.v}</div></div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── SLIDE 3 — DOCUMENTS ───────────────────────────────────────
  function renderS3() {
    return (
      <div>
        <div style={{background:'#F8FAFC',borderRadius:7,padding:'9px 13px',marginBottom:14,fontSize:13,display:'flex',alignItems:'center',gap:8}}>
          🌐 {t('polices.doc_language')}
        </div>
        {isProjet&&<div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:7,padding:'9px 13px',marginBottom:14,fontSize:13,color:'#92400E'}}>
          {t('amendements.doc_project_warning')}
        </div>}

        {/* Dossier projet */}
        <div style={{fontSize:11,fontWeight:700,color:'#64748B',textTransform:'uppercase',letterSpacing:'.06em',paddingBottom:6,borderBottom:'1px solid #F1F5F9',marginBottom:10}}>
          {t('amendements.doc_section_project')}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 13px',border:'1px solid #E2E8F0',borderRadius:8,marginBottom:8,background:'#fff'}}>
          <span style={{fontSize:22}}>📄</span>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:700}}>{t('amendements.doc_project')}</div>
            <div style={{fontSize:12,color:'#64748B',marginTop:2}}>{amend.n_amendement} · {t('amendements.doc_project_desc')}</div>
          </div>
          <button onClick={()=>alert(t('common.generate_coming_soon'))}
            style={{fontSize:13,padding:'5px 13px',borderRadius:6,cursor:'pointer',background:'#F1F5F9',color:'#64748B',border:'1px solid #E2E8F0',fontWeight:600}}>
            {t('common.download')} ({t('common.generate_coming_soon')})
          </button>
        </div>

        {/* Pièces contractuelles */}
        <div style={{fontSize:11,fontWeight:700,color:'#64748B',textTransform:'uppercase',letterSpacing:'.06em',padding:'14px 0 6px',borderBottom:'1px solid #F1F5F9',marginBottom:10}}>
          {t('amendements.doc_section_contracts')}
        </div>
        {[
          {key:'police',      label:t('polices.doc_policy'),      desc:`${t('polices.policy_number')} · ${amend.n_police||'—'}`, icon:'📋'},
          {key:'attestation', label:t('polices.doc_attestation'), desc:t('polices.doc_attestation_desc'), icon:'🏅'},
          {key:'cg',          label:t('polices.doc_cg'),          desc:'CG-F-AIE-XXI-2602', icon:'📘'},
        ].map(doc=>(
          <div key={doc.key} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 13px',border:'1px solid #E2E8F0',borderRadius:8,marginBottom:8,opacity:isProjet?.4:1,background:'#fff'}}>
            <span style={{fontSize:22}}>{doc.icon}</span>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700}}>{doc.label}</div>
              <div style={{fontSize:12,color:'#64748B',marginTop:2}}>{doc.desc}</div>
            </div>
            {isProjet
              ? <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:'#F1F5F9',color:'#94A3B8',border:'1px solid #E2E8F0'}}>{t('common.locked_after_activation')}</span>
              : <button onClick={()=>dlDoc(id,doc.key)} style={{fontSize:13,padding:'5px 13px',borderRadius:6,cursor:'pointer',background:'#1A1A1A',color:'#fff',border:'none',fontWeight:700}}>
                  {t('common.download')}
                </button>
            }
          </div>
        ))}

        {/* Appel de prime / Régularisation */}
        <div style={{fontSize:11,fontWeight:700,color:'#64748B',textTransform:'uppercase',letterSpacing:'.06em',padding:'14px 0 6px',borderBottom:'1px solid #F1F5F9',marginBottom:10}}>
          {t('amendements.doc_appel_section')}
        </div>
        {sens==='augmentation'&&(
          <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 13px',border:'1px solid #93C5FD',borderRadius:8,background:'#EFF6FF',marginBottom:8,opacity:isProjet?.4:1}}>
            <span style={{fontSize:22}}>💶</span>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700}}>{t('amendements.doc_appel_title')}</div>
              <div style={{fontSize:12,color:'#1D4ED8',marginTop:2}}>{t('amendements.doc_appel_amount')} : <strong>{fmt2(diff)}</strong></div>
            </div>
            {isProjet
              ? <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:'#DBEAFE',color:'#1D4ED8',border:'1px solid #93C5FD'}}>{t('common.locked_after_activation')}</span>
              : <button onClick={()=>alert(t('common.generate_coming_soon'))} style={{fontSize:13,padding:'5px 13px',borderRadius:6,cursor:'pointer',background:'#2563EB',color:'#fff',border:'none',fontWeight:700}}>{t('common.download')}</button>
            }
          </div>
        )}
        {(sens==='diminution'||sens==='stable')&&(
          <div style={{display:'flex',alignItems:'center',gap:12,padding:'10px 13px',border:'1px solid #6EE7B7',borderRadius:8,background:'#F0FDF4',marginBottom:8,opacity:isProjet?.4:1}}>
            <span style={{fontSize:22}}>📬</span>
            <div style={{flex:1}}>
              <div style={{fontSize:14,fontWeight:700}}>{t('amendements.doc_regularisation')}</div>
              <div style={{fontSize:12,color:'#065F46',marginTop:2}}>{t('amendements.doc_regularisation_desc')}</div>
            </div>
            {isProjet
              ? <span style={{fontSize:11,padding:'3px 9px',borderRadius:99,background:'#D1FAE5',color:'#065F46',border:'1px solid #6EE7B7'}}>{t('common.locked_after_activation')}</span>
              : <button onClick={()=>alert(t('common.generate_coming_soon'))} style={{fontSize:13,padding:'5px 13px',borderRadius:6,cursor:'pointer',background:'#059669',color:'#fff',border:'none',fontWeight:700}}>{t('common.download')}</button>
            }
          </div>
        )}
      </div>
    );
  }

  // ── Modals + Fiche ────────────────────────────────────────────
  return (
    <>
      {/* Modal Activation */}
      {showActivation&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:420,maxWidth:'90vw'}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:12}}>{t('amendements.confirm_activate_title')}</div>
            <div style={{fontSize:13,color:'#64748B',lineHeight:1.6,marginBottom:14}}>
              {t('amendements.tab_amendment')} <strong style={{color:'#1A1A1A'}}>{amend.n_amendement}</strong> {t('amendements.confirm_activate_desc')}<br/>
              <strong style={{color:'#1A1A1A'}}>{t('amendements.confirm_activate_irreversible')}</strong>
            </div>
            <div style={{background:'#F8FAFC',borderRadius:7,padding:'10px 12px',marginBottom:16,fontSize:13}}>
              <div>📋 {t('polices.doc_policy')}</div>
              <div>🏅 {t('polices.doc_attestation')}</div>
              {sens==='augmentation'&&<div>💶 {t('amendements.doc_appel_title')} <strong>{fmt2(diff)}</strong></div>}
              {(sens==='diminution'||sens==='stable')&&<div>📬 {t('amendements.doc_regularisation')}</div>}
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={()=>setShowActivation(false)} style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>{t('common.cancel')}</button>
              <button onClick={activer} disabled={activating} style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#059669',color:'#fff',border:'none',cursor:'pointer',fontWeight:700}}>
                {activating?t('common.saving'):'✓ '+t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Résiliation */}
      {showResil&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:480,maxWidth:'92vw',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:14}}>{t('resiliation.title_amendment')}</div>
            <div style={{marginBottom:14}}>
              <div style={{fontSize:12,color:'#64748B',marginBottom:8,fontWeight:700}}>{t('resiliation.motif_label')}</div>
              {MOTIFS.map(m=>(
                <button key={m.v} onClick={()=>{setResilMotif(m.v);setResilProrata(null);setResilDate('');setResilNotif('');}}
                  style={{display:'flex',alignItems:'flex-start',gap:10,width:'100%',padding:'10px 12px',marginBottom:6,
                    border:resilMotif===m.v?'1.5px solid #DC2626':'1px solid #E2E8F0',
                    borderRadius:8,cursor:'pointer',background:resilMotif===m.v?'#FFF5F5':'#fff',textAlign:'left'}}>
                  <span style={{fontSize:18,flexShrink:0}}>{m.icon}</span>
                  <div>
                    <div style={{fontSize:14,fontWeight:700,color:resilMotif===m.v?'#991B1B':'#1A1A1A'}}>{m.l}</div>
                    <div style={{fontSize:12,color:'#64748B',marginTop:2}}>{m.desc}</div>
                  </div>
                </button>
              ))}
            </div>
            {resilMotif==='aggravation_risque'&&(
              <div style={{marginBottom:12}}>
                <label style={lbl}>{t('resiliation.notif_date')}</label>
                <input type="date" value={resilNotif} onChange={e=>{setResilNotif(e.target.value);if(e.target.value){const d=new Date(e.target.value);d.setDate(d.getDate()+10);setResilProrata(calcProrata(d.toISOString().split('T')[0]));}}} style={inp}/>
                {resilNotif&&<div style={{fontSize:12,color:'#64748B',marginTop:4}}>{new Date(new Date(resilNotif).setDate(new Date(resilNotif).getDate()+10)).toLocaleDateString('fr-FR')} {t('resiliation.j_plus_10')}</div>}
              </div>
            )}
            {(resilMotif==='disparition_risque'||resilMotif==='non_paiement')&&(
              <div style={{marginBottom:12}}>
                <label style={lbl}>{t('resiliation.resil_date')}</label>
                <input type="date" value={resilDate} onChange={e=>{setResilDate(e.target.value);if(e.target.value&&MOTIFS.find(m=>m.v===resilMotif)?.prorata)setResilProrata(calcProrata(e.target.value));}} style={inp}/>
              </div>
            )}
            {resilProrata&&(
              <div style={{background:'#FEF3C7',border:'1px solid #FDE68A',borderRadius:8,padding:'10px 12px',marginBottom:12}}>
                <div style={{fontSize:12,fontWeight:700,color:'#92400E',marginBottom:7}}>{t('resiliation.prorata_title')}</div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,fontSize:13}}>
                  <div><div style={{color:'#92400E',fontSize:11}}>{t('resiliation.prorata_total')}</div><strong>{resilProrata.joursTotal} {t('resiliation.prorata_days')}</strong></div>
                  <div><div style={{color:'#92400E',fontSize:11}}>{t('resiliation.prorata_remaining')}</div><strong>{resilProrata.joursRestants} {t('resiliation.prorata_days')}</strong></div>
                  <div><div style={{color:'#92400E',fontSize:11}}>{t('resiliation.prorata_refund')}</div><strong style={{fontSize:15}}>{fmt2(resilProrata.montant)}</strong></div>
                </div>
              </div>
            )}
            <div style={{marginBottom:14}}>
              <label style={lbl}>{t('resiliation.note_label')}</label>
              <textarea value={resilNote} onChange={e=>setResilNote(e.target.value)} placeholder={t('resiliation.note_placeholder')} rows={2} style={{...inp,resize:'vertical',fontFamily:'inherit'}}/>
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={()=>{setShowResil(false);setResilMotif('');}} style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>{t('common.cancel')}</button>
              <button disabled={!resilMotif||resilSaving||(resilMotif==='aggravation_risque'&&!resilNotif)||(['disparition_risque','non_paiement'].includes(resilMotif)&&!resilDate)}
                onClick={resilier}
                style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#DC2626',color:'#fff',border:'none',cursor:'pointer',fontWeight:700,opacity:(!resilMotif||resilSaving)?.5:1}}>
                {resilSaving?t('common.saving'):t('resiliation.confirm_btn')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Suppression */}
      {showDelete&&(
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999}}>
          <div style={{background:'#fff',borderRadius:12,padding:24,width:380,maxWidth:'90vw'}}>
            <div style={{fontSize:15,fontWeight:700,marginBottom:12,color:'#DC2626'}}>{t('amendements.delete_title')}</div>
            <div style={{fontSize:13,color:'#64748B',lineHeight:1.6,marginBottom:16}}>
              {t('amendements.delete_confirm')} <strong>{amend.n_amendement}</strong> ? {t('common.irreversible')}
            </div>
            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button onClick={()=>setShowDelete(false)} style={{fontSize:14,padding:'7px 16px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>{t('common.cancel')}</button>
              <button onClick={supprimer} style={{fontSize:14,padding:'7px 16px',borderRadius:6,background:'#DC2626',color:'#fff',border:'none',cursor:'pointer',fontWeight:700}}>{t('common.yes')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Fiche */}
      <div style={{margin:'0 auto'}}>
        <div style={{background:'#fff',borderRadius:10,border:'1px solid #E2E8F0',overflow:'hidden'}}>
          {/* Header */}
          <div style={{padding:'11px 16px',borderBottom:'1px solid #F1F5F9',background:'#FFFBEB',display:'flex',alignItems:'center',gap:10}}>
            <button onClick={()=>navigate(-1)} style={{background:'none',border:'none',cursor:'pointer',color:'#92400E',fontSize:18}}>←</button>
            <div style={{flex:1}}>
              <div style={{fontSize:11,color:'#92400E'}}>{amend.client_nom} › {amend.n_police||amend.n_devis}</div>
              <div style={{fontSize:16,fontWeight:700,color:'#92400E'}}>{amend.n_amendement}</div>
            </div>
            <span style={{fontSize:11,padding:'3px 10px',borderRadius:99,background:sc.bg,color:sc.color,fontWeight:700,border:`1px solid ${sc.color}`}}>
              {STATUT_LABELS[amend.statut]}
            </span>
          </div>

          {/* Tabs */}
          <div style={{display:'flex',borderBottom:'1px solid #F1F5F9',padding:'0 14px',gap:2}}>
            {SLIDES.map((s,i)=>(
              <button key={i} onClick={()=>setSlide(i)}
                style={{padding:'9px 16px',fontSize:14,cursor:'pointer',background:'none',border:'none',
                  color:slide===i?'#D97706':'#64748B',fontWeight:700,
                  borderBottom:slide===i?'2px solid #D97706':'2px solid transparent'}}>
                {s}
              </button>
            ))}
          </div>

          {/* Contenu */}
          <div style={{padding:20,minHeight:400}}>
            {error&&<div style={{background:'#FEE2E2',color:'#991B1B',borderRadius:6,padding:'8px 12px',marginBottom:14,fontSize:14}}>{error}</div>}
            {isLocked&&amend.statut!=='resilie'&&(
              <div style={{background:'#D1FAE5',border:'1px solid #6EE7B7',borderRadius:7,padding:'8px 12px',marginBottom:14,fontSize:13,color:'#065F46'}}>
                🔒 {t('amendements.locked_banner')}
              </div>
            )}
            {slide===0&&renderS1()}
            {slide===1&&renderS2()}
            {slide===2&&renderS3()}
          </div>

          {/* Footer */}
          <div style={{padding:'10px 16px',borderTop:'1px solid #F1F5F9',background:'#FFFBEB',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',gap:7}}>
              <button onClick={()=>navigate(-1)} style={{fontSize:14,color:'#92400E',background:'none',border:'none',cursor:'pointer',padding:'6px 12px'}}>{t('common.close')}</button>
              <button onClick={()=>setShowDelete(true)}
                style={{fontSize:13,padding:'6px 12px',borderRadius:6,background:'#FEE2E2',color:'#991B1B',border:'1px solid #FCA5A5',cursor:'pointer',fontWeight:700}}>
                🗑 {t('common.delete')}
              </button>
            </div>
            <span style={{fontSize:12,color:'#92400E'}}>{t('common.slide')} {slide+1} / 3</span>
            <div style={{display:'flex',gap:7}}>
              {slide>0&&<button onClick={()=>setSlide(s=>s-1)} style={{fontSize:14,padding:'7px 14px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>{t('common.previous')}</button>}
              {slide<2&&<button onClick={()=>setSlide(s=>s+1)} style={{fontSize:14,padding:'7px 14px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>{t('common.next')}</button>}
              {isProjet&&(
                <button onClick={save} disabled={saving} style={{fontSize:14,padding:'7px 14px',borderRadius:6,border:'1px solid #CBD5E1',cursor:'pointer',background:'#fff'}}>
                  {saving?t('common.saving'):'💾 '+t('common.save')}
                </button>
              )}
              {isProjet&&(
                <button onClick={()=>setShowActivation(true)} disabled={!amend.date_effet}
                  style={{fontSize:14,padding:'7px 14px',borderRadius:6,background:'#059669',color:'#fff',border:'none',cursor:'pointer',fontWeight:700,opacity:amend.date_effet?1:.5}}>
                  ✓ {t('amendements.activate_btn')}
                </button>
              )}
              {isActif&&(
                <button onClick={()=>setShowResil(true)}
                  style={{fontSize:14,padding:'7px 14px',borderRadius:6,background:'#DC2626',color:'#fff',border:'none',cursor:'pointer',fontWeight:700}}>
                  {t('resiliation.title_amendment')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
